import { Fragment } from 'react';
import { App, ConfigProvider, Divider, Flex, Form, Modal, Space, Typography, theme } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileShield } from '@fortawesome/free-solid-svg-icons';
import { useUploadInsuranceCarrierCertificate } from '@features/insuranceCarriers/hooks/useInsuranceCarriers';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { CertificateSection, CERTIFICATE_FORMATS, FORMAT_LABELS } from './CertificateSection';

const { Text } = Typography;

export const UploadCertificateModal = ({ open, onCancel, record }) => {
  const [form] = Form.useForm();
  const { notification } = App.useApp();
  const { token: themeToken } = theme.useToken();
  const uploadInsuranceCarrierCertificateMutation = useUploadInsuranceCarrierCertificate();

  const certificates = record.certificates ?? [];

  const handleFinish = (values) => {
    const pfxCertificate = certificates.find((certificate) => certificate.format === CERTIFICATE_FORMATS.PFX);

    // TODO: Habilitar soporte para otros formatos en el backend
    if (!pfxCertificate || certificates.length > 1) {
      notification.warning({
        message: <Text strong>Función en desarrollo</Text>,
        description: `Actualmente solo está disponible la carga de un certificado en formato ${FORMAT_LABELS[CERTIFICATE_FORMATS.PFX]}. Soporte para otros formatos será habilitado próximamente.`,
      });
      return;
    }

    const { certificateFile, password } = values[pfxCertificate.type] ?? {};
    const file = certificateFile?.[0]?.originFileObj;

    uploadInsuranceCarrierCertificateMutation.mutate({ id: record.id, file, password }, {
      onSuccess: () => {
        notification.success({
          message: <Text strong>Certificado de <Text italic>{record.name}</Text> cargado</Text>,
          description: <>¡El certificado se ha cargado correctamente!</>,
        });
        onCancel();
      },
      onError: (error) => {
        notification.error({
          message: <Text strong>Certificado de <Text italic>{record.name}</Text> no cargado</Text>,
          description: getErrorMessage(error),
        });
      },
    });
  };

  if (!open || certificates.length === 0) return null;

  const title = (
    <Space>
      <FontAwesomeIcon icon={faFileShield} color={themeToken.colorPrimary} />
      <span>
        {certificates.length > 1 ? 'Cargar certificados' : 'Cargar certificado'} de {record.name}
      </span>
    </Space>
  );

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={certificates.length > 1 ? 'Cargar certificados' : 'Cargar certificado'}
      cancelText='Cancelar'
      maskClosable={false}
      closable={false}
      confirmLoading={uploadInsuranceCarrierCertificateMutation.isPending}
      destroyOnHidden
      width='40em'
    >
      <ConfigProvider
        theme={{
          components: {
            Form: {
              itemMarginBottom: 0, // Evita el espaciado por defecto de Form.Item; la separación se maneja mediante Flex gap
            },
          },
        }}
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleFinish}
          clearOnDestroy
        >
          <Flex vertical gap='small'>
            {certificates.map((certificate, index) => (
              <Fragment key={certificate.type}>
                {index > 0 && <Divider size='small' />}
                <CertificateSection certificate={certificate} />
              </Fragment>
            ))}
          </Flex>
        </Form>
      </ConfigProvider>
    </Modal>
  );
};
